import type { Locale } from '@/i18n/locales';

/**
 * Generic per-language keyterms biasing for ElevenLabs Scribe. Keep this list
 * truly cross-tenant — domain words (flight/hotel/vacation jargon) belong on
 * the tenant via `meta.dictationKeyterms`. Empty by default; we extend per
 * locale only when a homophone affects every tenant in that language.
 *
 * Limits: keep each entry ≤20 chars; combined with tenant terms ≤50 total.
 */
const DEFAULT_KEYTERMS_BY_LOCALE: Partial<Record<Locale, readonly string[]>> = {};

const MAX_KEYTERMS = 50;
const MAX_TERM_LENGTH = 20;

export function resolveDictationKeyterms({
  locale,
  tenantKeyterms,
}: {
  locale: string;
  tenantKeyterms?: readonly string[];
}): readonly string[] {
  const defaults = DEFAULT_KEYTERMS_BY_LOCALE[locale as Locale] ?? [];
  const merged = [...defaults, ...(tenantKeyterms ?? [])]
    .map(term => term.trim())
    .filter(term => term.length > 0 && term.length <= MAX_TERM_LENGTH);
  // Dedupe (preserving first-seen order) and clamp to Scribe's 50-term cap.
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const term of merged) {
    if (seen.has(term)) continue;
    seen.add(term);
    unique.push(term);
    if (unique.length === MAX_KEYTERMS) break;
  }
  return unique;
}
